import React from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { useInView } from "react-intersection-observer";

import {
  Icon,
  VideoPlayer,
  baseRenderContentType,
  LinkGenerator,
  Image,
  RichText,
  constructSizes
} from "@lululemon/ui-library";
import * as AllIcons from "@lululemon/ui-icons";
import { ErrorBoundary } from "@lululemon/mwa-logger";
import { renderContentfulComponent } from "@lululemon/cosy-contentful-renderer";

import {
  DESKTOP_GRID_BREAKPOINT,
  MAX_GRID_WIDTH,
  TABLET_GRID_BREAKPOINT
} from "../../common/lib/breakpoints";
import { productEdHandleError } from "../../utils/product-education-logger";

import styles from "./franchise-comparison-item.module.scss";

const IMAGE_ASSET_HEIGHT = 374;
const IMAGE_ASSET_WIDTH = 312;
const IMAGE_ASSET_MAXWIDTH = 624;

const isAdaptiveVideo = (videoUrl) => !!videoUrl?.includes(".m3u8");

const ActivityIcon = ({ iconName = "" }) => {
  const IconComponent = AllIcons[iconName];

  if (!IconComponent) return null;

  return <Icon className={styles.activityIcon} content={IconComponent} />;
};

function FranchiseComparisonItem({
  activityIcon,
  activityText,
  button,
  franchiseComparisonItemDetails,
  franchiseImgCallbackRef,
  franchiseName,
  index,
  media,
  handleButtonLinkClick
}) {
  const { ref, inView } = useInView({
    triggerOnce: true
  });

  const headingId = `franchise-comparison-item-hdg-${index}`;
  const hasActivityIconOrText = !!activityIcon || !!activityText;

  const mediaType = media?.sys?.contentType?.sys?.id; // video || responsiveImage
  const {
    imageUrl,
    mobileImageUrl,
    description,
    videoUrl,
    videoPreviewImageUrl,
    portraitVideoUrl,
    portraitVideoPreviewImageUrl,
    minimalButtonAlignment
  } = media?.fields || {};

  return (
    <ErrorBoundary
      onError={(error) =>
        productEdHandleError(
          error,
          "Franchise Comparison Item",
          "Franchise Comparison"
        )
      }
    >
      <div
        aria-labelledby={headingId}
        aria-roledescription="slide"
        className={cs(styles.columnWrapper)}
        role="group"
      >
        {mediaType === "responsiveImage" && (
          <Image
            alt={description}
            block
            className={styles.image}
            draggable={false}
            height={IMAGE_ASSET_HEIGHT}
            maxWidth={IMAGE_ASSET_MAXWIDTH}
            mobileSrc={mobileImageUrl}
            ref={index === 0 ? franchiseImgCallbackRef : undefined}
            sizes={constructSizes([
              {
                breakpoint: MAX_GRID_WIDTH,
                slotSize: `${IMAGE_ASSET_WIDTH}px`
              },
              { breakpoint: DESKTOP_GRID_BREAKPOINT, slotSize: "22vw" },
              { breakpoint: TABLET_GRID_BREAKPOINT, slotSize: "22vw" },
              { slotSize: "43vw" }
            ])}
            src={imageUrl}
            tag="div"
          />
        )}
        {mediaType === "video" && (
          <>
            {/* Empty span to use for intersection observer */}
            <span aria-hidden={true} ref={ref} />
            {inView ? (
              <VideoPlayer
                autoplay={true}
                variant="minimal"
                minimalButtonAlignment={minimalButtonAlignment || "bottomRight"}
                allowAudio={false}
                allowFullscreen={false}
                className={styles.fcVideoContainer}
                videoClassName={styles.fcVideo}
                landscapeSource={
                  !isAdaptiveVideo(videoUrl) ? videoUrl : undefined
                }
                landscapeAdaptiveSource={
                  isAdaptiveVideo(videoUrl) ? videoUrl : undefined
                }
                landscapePosterImageProps={{ src: videoPreviewImageUrl }}
                portraitSource={
                  !isAdaptiveVideo(portraitVideoUrl)
                    ? portraitVideoUrl
                    : undefined
                }
                portraitAdaptiveSource={
                  isAdaptiveVideo(portraitVideoUrl)
                    ? portraitVideoUrl
                    : undefined
                }
                portraitPosterImageProps={{
                  src: portraitVideoPreviewImageUrl
                }}
              />
            ) : null}
          </>
        )}
        {hasActivityIconOrText && (
          <div className={cs("lll-text-body-3", styles.activity)}>
            {!!activityIcon && <ActivityIcon iconName={activityIcon} />}
            {!!activityText && activityText}
          </div>
        )}
        {!!franchiseName && (
          <h3 className={styles.name} id={headingId}>
            {franchiseName}
          </h3>
        )}
        <>
          {franchiseComparisonItemDetails.map((itemDetails, childIndex) => {
            const { description, title } = itemDetails?.fields || {};
            const descriptionType = description?.sys?.contentType?.sys?.id;
            return (
              <div
                className={styles.itemDetails}
                // eslint-disable-next-line react/no-array-index-key
                key={`${title}-${childIndex}`}
              >
                {title && <h4 className={styles.itemDetailsTitle}>{title}</h4>}
                {descriptionType === "linkList" && (
                  <ul className={styles.description}>
                    {description?.fields?.links?.map((link) => {
                      const { callToAction, webRoute } = link?.fields || {};
                      return (
                        <li
                          key={link?.sys?.id}
                          className={cs(styles.descriptionListItem)}
                        >
                          <LinkGenerator
                            className="lll-link-primary"
                            href={webRoute}
                            onClick={() =>
                              handleButtonLinkClick("link", callToAction)
                            }
                          >
                            {callToAction}
                          </LinkGenerator>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {descriptionType === "richTextBody" && (
                  <RichText
                    body={description?.fields?.body}
                    className={cs(styles.descriptionText, styles.description)}
                    customEntryRenderer={renderContentfulComponent}
                    options={{ renderContentType: baseRenderContentType }}
                  />
                )}
              </div>
            );
          })}
        </>
        {!!button && (
          <div className={styles.buttonWrapper}>
            {renderContentfulComponent({
              contentfulComponent: button,
              options: {
                renderContentType: baseRenderContentType,
                transformElement: ({ element }) => {
                  const extraProps = {
                    block: true,
                    className: styles.button,
                    kind: "tertiary",
                    mobileBlock: false,
                    onClick: () =>
                      handleButtonLinkClick("button", element.props.children)
                  };

                  return React.cloneElement(element, extraProps);
                }
              }
            })}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

FranchiseComparisonItem.defaultProps = {
  activityIcon: "",
  activityText: "",
  button: undefined,
  franchiseComparisonItemDetails: [],
  franchiseImgCallbackRef: undefined,
  franchiseName: "",
  image: undefined,
  index: 0
};

FranchiseComparisonItem.propTypes = {
  activityIcon: PropTypes.string,
  activityText: PropTypes.string,
  button: PropTypes.object,
  franchiseComparisonItemDetails: PropTypes.array,
  franchiseImgCallbackRef: PropTypes.func,
  franchiseName: PropTypes.string,
  image: PropTypes.object,
  index: PropTypes.number,
  handleButtonLinkClick: PropTypes.func
};

export default FranchiseComparisonItem;
